import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { supabase } from "@/integrations/supabase/client";
import { SectionHeader } from "../shared/SectionHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

type PaymentRow = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  reference: string;
};

export function SubscriptionBilling() {
  const { profile } = useAuth();
  const { openPaywall } = usePaywallModal();
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const plan = (profile?.subscription_plan ?? "free") as string;
  const status = (profile?.subscription_status ?? "inactive") as string;
  const validUntil = profile?.subscription_valid_until ?? null;
  const isFounding = !!profile?.is_founding_member;
  const isInGrace = status === "grace";

  const planLabel = plan === "pro_growth" ? "Pro Growth" : plan === "pro_solo" ? "Pro Solo" : "Free";
  const daysAway = validUntil
    ? Math.ceil((new Date(validUntil).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null;
  const graceDaysRemaining = isInGrace && validUntil ? Math.max(0, daysAway ?? 0) : null;

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("payments")
        .select("id, amount, currency, status, created_at, reference")
        .order("created_at", { ascending: false })
        .limit(20);
      setPayments((data ?? []) as PaymentRow[]);
      setPaymentsLoading(false);
    };
    load();
  }, []);

  return (
    <>
      <SectionHeader
        title="Subscription & Billing"
        description="Manage your plan, payment method, and view past payments."
      />
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={plan !== "free" ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}>
              {planLabel}
            </Badge>
            {isFounding && (
              <Badge className="bg-amber-500/20 text-amber-600 dark:text-amber-400">Founding Member</Badge>
            )}
          </div>
          {validUntil && (
            <p className="text-[12px] text-muted-foreground mt-1">
              Next billing: {new Date(validUntil).toLocaleDateString()}
              {daysAway != null && ` (${daysAway} days away)`}
            </p>
          )}
          <p className="text-[12px] text-muted-foreground">Payment method on file: Card / Lenco</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {isInGrace && (
            <div className="border border-amber-500/50 rounded-md p-3 text-amber-700 dark:text-amber-200 text-sm">
              Grace period — {graceDaysRemaining != null ? graceDaysRemaining : 0} days remaining.{" "}
              <Button variant="link" className="p-0 h-auto text-amber-600 dark:text-amber-400" onClick={() => openPaywall("grace")}>
                Pay Now
              </Button>
            </div>
          )}
          {plan !== "pro_growth" && (
            <Button onClick={() => openPaywall("upgrade")}>Upgrade Plan</Button>
          )}
        </CardContent>
      </Card>
      <div className="mt-6">
        <p className="text-[13px] font-medium text-muted-foreground mb-2">Payment history</p>
        {paymentsLoading ? (
          <p className="text-[12px] text-muted-foreground">Loading…</p>
        ) : payments.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">Your past payments will appear here.</p>
        ) : (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Date</th>
                  <th className="text-left py-2 px-3 font-medium">Amount</th>
                  <th className="text-left py-2 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="py-2 px-3 text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-3">
                      {p.currency} {p.amount.toLocaleString()}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground capitalize">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
